import { useActionConfirmation } from '../components/ActionFeedbackProvider';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, ChevronDown, ChevronUp, ClipboardList, Home, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import CreateOrderModal from '../components/modals/CreateOrderModal';

const STATUS_STYLES = {
  PENDING: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
  PARTIAL: 'bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200',
  COMPLETED: 'bg-green-100 text-green-900 dark:bg-green-900/40 dark:text-green-200',
};

const formatDate = (value, language) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(language === 'es' ? 'es-MX' : 'en-US', {
    dateStyle: 'medium', timeStyle: 'short',
  }).format(date);
};

const formatCurrency = (value, language) => new Intl.NumberFormat(language === 'es' ? 'es-MX' : 'en-US', {
  style: 'currency', currency: 'MXN',
}).format(value);

const orderEstimate = (order) => {
  const items = Array.isArray(order.items) ? order.items : [];
  if (!items.length || items.some((item) => item.estimatedTotal === null || item.estimatedTotal === undefined)) return null;
  return items.reduce((sum, item) => sum + Number(item.estimatedTotal || 0), 0);
};

const OrderScreen = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const confirmAction = useActionConfirmation();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingOrderId, setDeletingOrderId] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/orders');
      setOrders(Array.isArray(response.data) ? response.data : []);
    } catch (fetchError) {
      console.error('Error fetching orders', fetchError);
      setError(t('ordersLoadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleDeleteOrder = async (order) => {
    const orderId = order.id || order._id;
    if (!orderId || !await confirmAction(t('confirmDeleteOrder', { orderNumber: order.orderNumber || orderId }))) return;
    setDeletingOrderId(orderId);
    setError('');
    try {
      await api.delete(`/orders/${orderId}`);
      setOrders((current) => current.filter((item) => (item.id || item._id) !== orderId));
      if (expandedOrderId === orderId) setExpandedOrderId(null);
    } catch (deleteError) {
      console.error('Error deleting order', deleteError);
      setError(t('orderDeleteError'));
    } finally {
      setDeletingOrderId(null);
    }
  };

  const filteredOrders = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return [...orders]
      .filter((order) => statusFilter === 'ALL' || order.status === statusFilter)
      .filter((order) => !query || [order.clientName, order.orderNumber, order.notes]
        .some((value) => String(value || '').toLocaleLowerCase().includes(query)))
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [orders, search, statusFilter]);

  const counts = useMemo(() => orders.reduce((result, order) => {
    const status = order.status || 'PENDING';
    result[status] = (result[status] || 0) + 1;
    return result;
  }, {}), [orders]);

  const baseBtn = 'inline-flex items-center justify-center gap-2 rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-black dark:hover:bg-gray-200';
  const inputClass = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white';

  const statusLabel = (status) => t(`orderStatus${(status || 'PENDING').charAt(0)}${(status || 'PENDING').slice(1).toLowerCase()}`);

  return (
    <div className="min-h-full p-4 md:p-6">
      <div className="mb-5 flex flex-col items-center gap-3">
        <button className={baseBtn} onClick={() => navigate('/')}><Home size={17} />{t('home')}</button>
        <button className={baseBtn} onClick={fetchOrders} disabled={loading} aria-label={t('refreshOrders')}><RefreshCw size={17} className={loading ? 'animate-spin' : ''} />{t('refreshOrders')}</button>
      </div>

      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <div className="rounded-full bg-blue-100 p-3 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200"><ClipboardList size={26} /></div>
          <h1 className="text-3xl font-extrabold">{t('orders')}</h1>
          <p className="max-w-2xl text-sm text-gray-600 dark:text-gray-300">{t('ordersIntro')}</p>
        </div>

        <div className="mb-6 flex justify-center">
          <button className={baseBtn} onClick={() => setShowCreateModal(true)}><Plus size={18} />{t('addOrder')}</button>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {['PENDING', 'PARTIAL', 'COMPLETED'].map((status) => (
            <button key={status} onClick={() => setStatusFilter(statusFilter === status ? 'ALL' : status)} className={`rounded-xl border p-4 text-left shadow-sm transition ${statusFilter === status ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-900' : 'border-gray-200 dark:border-gray-700'} bg-white/90 dark:bg-gray-800/90`}>
              <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_STYLES[status]}`}>{statusLabel(status)}</span>
              <span className="mt-2 block text-2xl font-extrabold">{counts[status] || 0}</span>
            </button>
          ))}
        </div>

        <div className="mb-4 flex flex-col gap-3 sm:flex-row">
          <label className="relative flex-1">
            <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className={`${inputClass} pl-10`} type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('searchOrders')} />
          </label>
          <select className={`${inputClass} sm:w-56`} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="ALL">{t('allOrderStatuses')}</option>
            <option value="PENDING">{statusLabel('PENDING')}</option>
            <option value="PARTIAL">{statusLabel('PARTIAL')}</option>
            <option value="COMPLETED">{statusLabel('COMPLETED')}</option>
          </select>
        </div>

        {error && <div role="alert" className="mb-4 rounded-lg bg-red-100 p-3 text-red-900 dark:bg-red-900/40 dark:text-red-100">{error}</div>}
        {loading ? <p className="py-10 text-center">{t('loading')}</p> : filteredOrders.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white/80 p-10 text-center text-gray-600 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-300">
            <ClipboardList className="mx-auto mb-3 opacity-50" size={34} />
            <p className="font-semibold">{search || statusFilter !== 'ALL' ? t('noMatchingOrders') : t('noOrders')}</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white/90 shadow-sm dark:border-gray-700 dark:bg-gray-800/90">
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredOrders.map((order, index) => {
                const id = order.id || order._id || `${order.orderNumber}-${index}`;
                const expanded = expandedOrderId === id;
                const itemCount = Array.isArray(order.items) ? order.items.length : 0;
                const estimate = orderEstimate(order);
                return (
                  <article key={id}>
                    <div className="grid grid-cols-[1fr_auto] items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/60">
                      <div className="min-w-0">
                        <div className="mb-1 flex flex-wrap items-center gap-2">
                          <h2 className="truncate text-lg font-bold">{order.clientName || t('unnamedCustomer')}</h2>
                          <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_STYLES[order.status] || STATUS_STYLES.PENDING}`}>{statusLabel(order.status)}</span>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
                          <span>{t('orderNumber')}: {order.orderNumber || String(id).slice(-6)}</span>
                          <span>{itemCount} {t('orderItems').toLocaleLowerCase(i18n.language)}</span>
                          <span className="inline-flex items-center gap-1"><CalendarDays size={14} />{formatDate(order.createdAt, i18n.language)}</span>
                          {estimate !== null && <span className="font-semibold text-gray-900 dark:text-white">{t('estimatedOrderTotal')}: {formatCurrency(estimate, i18n.language)}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => handleDeleteOrder(order)} disabled={deletingOrderId === id} title={t('deleteOrder')} aria-label={`${t('deleteOrder')}: ${order.orderNumber || order.clientName || id}`} className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-red-600 text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50">
                          {deletingOrderId === id ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" /> : <Trash2 size={16} />}
                        </button>
                        <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-600" onClick={() => setExpandedOrderId(expanded ? null : id)} aria-expanded={expanded} aria-label={expanded ? t('collapseOrder') : t('expandOrder')}>
                          {expanded ? <ChevronUp /> : <ChevronDown />}
                        </button>
                      </div>
                    </div>

                    {expanded && (
                      <div className="border-t border-gray-200 bg-gray-50/80 px-4 py-4 dark:border-gray-700 dark:bg-gray-900/30">
                        <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {order.items?.map((item, itemIndex) => (
                            <div key={`${item.productTemplateId || item.productName}-${itemIndex}`} className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                              <p className="font-semibold">{item.productName || t('unnamedProduct')}</p>
                              <div className="mt-1 flex flex-wrap gap-x-4 text-sm text-gray-600 dark:text-gray-300">
                                {item.priceUnit === 'PIECE' ? (
                                  item.expectedQuantity != null && <span>{t('expectedPieces')}: {Number(item.deliveredQuantity || 0).toLocaleString(i18n.language)} / {Number(item.expectedQuantity).toLocaleString(i18n.language)}</span>
                                ) : (
                                  <>
                                    {item.expectedBoxes != null && <span>{t('boxes')}: {item.deliveredBoxes || 0} / {item.expectedBoxes}</span>}
                                    {item.expectedWeight != null && <span>{t('weight')}: {Number(item.deliveredWeight || 0).toLocaleString(i18n.language)} / {Number(item.expectedWeight).toLocaleString(i18n.language)} KG</span>}
                                  </>
                                )}
                                {item.unitPrice != null && <span>{t('snapshotUnitPrice')}: {formatCurrency(Number(item.unitPrice), i18n.language)} / {item.priceUnit === 'PIECE' ? t('pieceUnit') : 'KG'}</span>}
                                {item.estimatedTotal != null && <span className="font-semibold">{t('estimatedLineTotal')}: {formatCurrency(Number(item.estimatedTotal), i18n.language)}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                        {order.notes && <p className="whitespace-pre-wrap text-sm"><span className="font-semibold">{t('notes')}:</span> {order.notes}</p>}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {showCreateModal && <CreateOrderModal isOpen onClose={() => setShowCreateModal(false)} onSaved={fetchOrders} />}
    </div>
  );
};

export default OrderScreen;
